import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useCallbackRef } from 'use-callback-ref';

import Popover from 'app/components/v2/components/Popover';

import List, { ListProps } from './List';
import BodyEnd from '../BodyEnd';
import { Coords } from '../utils/lib/dom';

// Local Typings
type AnchorPosition = 'bottom' | 'top';
interface ListContainerProps {
  align?: 'left' | 'right';
  anchor?: AnchorPosition;
  coverAnchorEl?: boolean;
  maxHeight?: number;
}
export type MenuProps = ListContainerProps & ListProps & {
  anchorEl: HTMLElement;
  isOpen: boolean;
  lockScrollElements?: (HTMLElement | null)[];
  onClose?: () => void;
  withBodyEnd?: boolean;
};
interface GetPositionProps {
  align?: ListContainerProps['align'];
  boundingClientRect: DOMRect;
  placement: AnchorPosition;
}

// Local Variables
function getMenuPosition({
  boundingClientRect,
  placement,
}: GetPositionProps): Coords & { isInYView: boolean } {
  const {
    height,
    width,
  } = boundingClientRect;

  const windowHeight = window.innerHeight;

  let top = height && placement === 'top'
    ? boundingClientRect.top + height / 2
    : boundingClientRect.top;
  const left = width && boundingClientRect.left
    ? boundingClientRect.left + width / 2
    : boundingClientRect.left;

  if (placement === 'bottom') {
    top = boundingClientRect.bottom;
  } else if (placement === 'top') {
    top = boundingClientRect.top;
  }

  const isInYView = placement === 'top'
    ? height + top < windowHeight
    : boundingClientRect.top > 0;

  return {
    isInYView,
    left,
    top,
  };
}

// Component Definition
const Menu: FC<MenuProps> = props => {
  const [position, setPosition] = useState<AnchorPosition>(props.anchor ?? 'top');
  const [listContainerEl, setListContainerEl] = useState<HTMLUListElement | null>(null);
  const [hasPositionChanged, setHasPositionChanged] = useState(false);

  const listContainerRef = useCallbackRef<HTMLUListElement>(null,
    setListContainerEl);

  function handleClose() {
    if (props.onClose) {
      props.onClose();
    }
  }

  // TODO: Remove this eslint disable once we have time to come back and fix this
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleChange(id: number | string) {
    props.onChange(id);

    if (!props.values) {
      handleClose();
    }
  }

  const flipY = useCallback(() => {
    if (position === 'top') {
      setPosition('bottom');
    } else {
      setPosition('top');
    }

    setHasPositionChanged(true);
  },
  [position]);

  const menuPosition = listContainerEl
    ? getMenuPosition({
      boundingClientRect: listContainerEl.getBoundingClientRect(),
      placement: props.anchor ?? 'top',
    })
    : null;

  useEffect(() => {
    setListContainerEl(listContainerRef.current);
  },
  [listContainerRef]);

  useEffect(() => {
    if (menuPosition && !menuPosition.isInYView && !hasPositionChanged) {
      flipY();
    }
  },
  [flipY, hasPositionChanged, menuPosition]);

  useEffect(() => {
    if (!props.isOpen) {
      setHasPositionChanged(false);
    }
  },
  [props.isOpen]);

  const sharedListProps = useMemo<Omit<ListProps, 'value' | 'values'>>(() => ({
    onChange: handleChange,
    options: props.options,
    shouldDisableSelectOnSpace: props.shouldDisableSelectOnSpace,
    shouldStopPropagation: props.shouldStopPropagation,
  }),
  [
    handleChange,
    props.shouldDisableSelectOnSpace,
    props.options,
    props.shouldStopPropagation,
  ]);

  if (!props.isOpen) {
    return null;
  }

  const content = (
    <Popover
      align={props.align}
      anchor={props.anchor}
      anchorEl={props.anchorEl}
      className="menu"
      coverAnchorEl={props.coverAnchorEl}
      isOpen={props.isOpen}
      lockScrollElements={props.lockScrollElements}
      maxHeight={props.maxHeight}
      onClose={props.onClose}
      withBodyEnd={props.withBodyEnd}
    >
      {props.values
        ? (
          <List
            {...sharedListProps}
            values={props.values}
          />
        )
        : (
          <List
            {...sharedListProps}
            value={props.value}
          />
        )}
    </Popover>
  );

  return props.withBodyEnd
    ? (
      <BodyEnd>
        {content}
      </BodyEnd>
    )
    : content;
};

// Exports
export default Menu;
